/**
 * TreePanel — collapsible inverter → string asset tree with search.
 */

import { useState, useMemo } from 'react';
import './TreePanel.css';

export type NodeStatus = 'active' | 'idle' | 'fault' | 'warning';

export interface StringNode {
  id: string;
  label: string;
  power: number;
  status: NodeStatus;
}

export interface InverterNode {
  id: string;
  label: string;
  status: NodeStatus;
  strings: StringNode[];
}

interface TreePanelProps {
  inverters?: InverterNode[];
  selectedId?: string;
  onSelect?: (id: string | null) => void;
}

// Mock data: 4 inverters × 3–6 strings
const MOCK_INVERTERS: InverterNode[] = [
  {
    id: 'inv-1', label: 'INV-01', status: 'active',
    strings: [
      { id: 'str-1-1', label: 'STR 1.1', power: 0.94, status: 'active' },
      { id: 'str-1-2', label: 'STR 1.2', power: 0.87, status: 'active' },
      { id: 'str-1-3', label: 'STR 1.3', power: 0.00, status: 'fault'  },
    ],
  },
  {
    id: 'inv-2', label: 'INV-02', status: 'active',
    strings: [
      { id: 'str-2-1', label: 'STR 2.1', power: 0.91, status: 'active' },
      { id: 'str-2-2', label: 'STR 2.2', power: 0.88, status: 'active' },
      { id: 'str-2-3', label: 'STR 2.3', power: 0.00, status: 'idle'   },
      { id: 'str-2-4', label: 'STR 2.4', power: 0.92, status: 'active' },
    ],
  },
  {
    id: 'inv-3', label: 'INV-03', status: 'warning',
    strings: [
      { id: 'str-3-1', label: 'STR 3.1', power: 0.56, status: 'active' },
      { id: 'str-3-2', label: 'STR 3.2', power: 0.61, status: 'active' },
      { id: 'str-3-3', label: 'STR 3.3', power: 0.59, status: 'active' },
      { id: 'str-3-4', label: 'STR 3.4', power: 0.58, status: 'active' },
      { id: 'str-3-5', label: 'STR 3.5', power: 0.54, status: 'active' },
    ],
  },
  {
    id: 'inv-4', label: 'INV-04', status: 'active',
    strings: [
      { id: 'str-4-1', label: 'STR 4.1', power: 0.93, status: 'active' },
      { id: 'str-4-2', label: 'STR 4.2', power: 0.90, status: 'active' },
      { id: 'str-4-3', label: 'STR 4.3', power: 0.00, status: 'idle'   },
      { id: 'str-4-4', label: 'STR 4.4', power: 0.89, status: 'active' },
      { id: 'str-4-5', label: 'STR 4.5', power: 0.00, status: 'fault'  },
      { id: 'str-4-6', label: 'STR 4.6', power: 0.91, status: 'active' },
    ],
  },
];

function StatusDot({ status }: { status: NodeStatus }) {
  return <span className={`tree-status tree-status--${status}`} aria-hidden />;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 16 16"
      fill="none" stroke="currentColor" strokeWidth="2"
      className={`tree-chevron${open ? ' tree-chevron--open' : ''}`}
    >
      <path d="M4 6l4 4 4-4"/>
    </svg>
  );
}

export default function TreePanel({ inverters = MOCK_INVERTERS, selectedId, onSelect }: TreePanelProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['inv-1']));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(selectedId ?? null);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelect = (id: string) => {
    const next = selected === id ? null : id;
    setSelected(next);
    onSelect?.(next);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return inverters;
    const q = search.toLowerCase();
    return inverters
      .map((inv) => ({
        ...inv,
        strings: inv.strings.filter(
          (s) => s.label.toLowerCase().includes(q) || s.id.toLowerCase().includes(q)
        ),
      }))
      .filter((inv) => inv.label.toLowerCase().includes(q) || inv.strings.length > 0);
  }, [inverters, search]);

  return (
    <div className="tree-panel">
      {/* Search */}
      <div className="tree-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="tree-search__icon">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          type="search"
          className="tree-search__input"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tree */}
      <div className="tree-body" role="tree">
        {filtered.map((inv) => {
          const isOpen = expandedIds.has(inv.id) || search.length > 0;

          return (
            <div key={inv.id} className="tree-group" role="treeitem" aria-expanded={isOpen}>
              {/* Inverter row */}
              <button
                type="button"
                className={`tree-row tree-row--inverter${selected === inv.id ? ' tree-row--selected' : ''}`}
                onClick={() => { toggleExpand(inv.id); handleSelect(inv.id); }}
              >
                <ChevronIcon open={isOpen} />
                <StatusDot status={inv.status} />
                <span className="tree-row__label">{inv.label}</span>
                <span className={`tree-badge tree-badge--${inv.status}`}>{inv.status}</span>
              </button>

              {/* String rows */}
              {isOpen && (
                <div className="tree-children" role="group">
                  {inv.strings.map((str) => (
                    <button
                      key={str.id}
                      type="button"
                      className={`tree-row tree-row--string${selected === str.id ? ' tree-row--selected' : ''}`}
                      onClick={() => handleSelect(str.id)}
                    >
                      <StatusDot status={str.status} />
                      <span className="tree-row__label">{str.label}</span>
                      <span className={`tree-row__value tabular tree-row__value--${str.status}`}>
                        {str.status === 'fault'
                          ? 'FAULT'
                          : str.status === 'idle'
                          ? 'Idle'
                          : `${str.power.toFixed(2)} kW`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="tree-empty">No results for "{search}"</div>
        )}
      </div>
    </div>
  );
}
